// Página Help - Página de ayuda e información
// Muestra FAQs, información de envíos, devoluciones, garantía y métodos de pago

import { useState, useEffect } from 'react'
import { getHelpSettings } from '../services/helpSettings.service'
import { buildWhatsAppLinkAR } from '../utils/whatsapp'
import { 
  FacebookIcon, 
  InstagramIcon, 
  WhatsAppIcon,
  LocationIcon,
  PhoneIcon,
  EmailIcon,
  ClockIcon
} from '../components/icons/SocialIcons'
import './Help.css'

/**
 * Página Help
 * Página pública con información de ayuda y soporte
 */
function Help() {
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedFAQ, setExpandedFAQ] = useState(null)

  // Cargar configuración de ayuda
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await getHelpSettings()
        setSettings(data)
      } catch (err) {
        console.error('Error cargando configuración de ayuda:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadSettings()
  }, [])

  // Scroll automático a sección si hay hash en la URL
  useEffect(() => {
    if (!loading && window.location.hash) {
      const hash = window.location.hash.substring(1)
      const element = document.getElementById(hash)
      if (element) {
        setTimeout(() => {
          const headerHeight = 80
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
          const offsetPosition = elementPosition - headerHeight
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }, 100)
      }
    }
  }, [loading])

  // Manejar toggle de FAQ
  const handleFAQToggle = (faqId) => {
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId)
  }

  // Ordenar FAQs por orden
  const sortedFAQs = settings?.faqs 
    ? [...settings.faqs].sort((a, b) => (a.order || 0) - (b.order || 0))
    : []

  if (loading) {
    return (
      <div className="help-page">
        <div className="help-loading">
          <p>Cargando información...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="help-page">
        <div className="help-error">
          <p>Error al cargar la información: {error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="help-page">
      <div className="container">
        {/* Header */}
        <div className="help-header">
          <h1>Centro de Ayuda</h1>
          <p>Encuentra respuestas a tus preguntas y toda la información que necesitas</p>
        </div>

        {/* Sección: Preguntas Frecuentes */}
        <section className="help-section" id="faq">
          <h2 className="help-section-title">Preguntas Frecuentes</h2>
          {sortedFAQs.length === 0 ? (
            <div className="help-empty">
              <p>No hay preguntas frecuentes disponibles en este momento.</p>
            </div>
          ) : (
            <div className="faqs-list">
              {sortedFAQs.map((faq) => (
                <div 
                  key={faq.id} 
                  className={`faq-item ${expandedFAQ === faq.id ? 'expanded' : ''}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => handleFAQToggle(faq.id)}
                    aria-expanded={expandedFAQ === faq.id}
                  >
                    <span>{faq.question}</span>
                    <span className="faq-icon">{expandedFAQ === faq.id ? '−' : '+'}</span>
                  </button>
                  {expandedFAQ === faq.id && (
                    <div 
                      className="faq-answer"
                      dangerouslySetInnerHTML={{ __html: faq.answer || 'Sin respuesta disponible.' }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Sección: Información de Envíos */}
        <section className="help-section" id="envios">
          <h2 className="help-section-title">Información de Envíos</h2>
          {settings?.shippingInfo ? (
            <div 
              className="help-content"
              dangerouslySetInnerHTML={{ __html: settings.shippingInfo }}
            />
          ) : (
            <div className="help-empty">
              <p>La información de envíos no está disponible en este momento.</p>
            </div>
          )}
        </section>

        {/* Sección: Cambios y Devoluciones */}
        <section className="help-section" id="devoluciones">
          <h2 className="help-section-title">Cambios y Devoluciones</h2>
          {settings?.returnsInfo ? (
            <div 
              className="help-content"
              dangerouslySetInnerHTML={{ __html: settings.returnsInfo }}
            />
          ) : (
            <div className="help-empty">
              <p>La información de cambios y devoluciones no está disponible en este momento.</p>
            </div>
          )}
        </section>

        {/* Sección: Garantía */}
        <section className="help-section" id="garantia">
          <h2 className="help-section-title">Garantía</h2>
          {settings?.warrantyInfo ? (
            <div 
              className="help-content"
              dangerouslySetInnerHTML={{ __html: settings.warrantyInfo }}
            />
          ) : (
            <div className="help-empty">
              <p>La información de garantía no está disponible en este momento.</p>
            </div>
          )}
        </section>

        {/* Sección: Métodos de Pago */}
        <section className="help-section" id="pagos">
          <h2 className="help-section-title">Métodos de Pago</h2>
          {settings?.paymentMethods ? (
            <div 
              className="help-content"
              dangerouslySetInnerHTML={{ __html: settings.paymentMethods }}
            />
          ) : (
            <div className="help-empty">
              <p>La información de métodos de pago no está disponible en este momento.</p>
            </div>
          )}
        </section>

        {/* Sección: Contacto */}
        {settings?.contact && (
          (settings.contact.address || 
           settings.contact.phone || 
           settings.contact.email || 
           settings.contact.schedule ||
           settings.contact.socialMedia?.facebook ||
           settings.contact.socialMedia?.instagram ||
           settings.contact.socialMedia?.whatsapp) && (
            <section className="help-section" id="contacto">
              <h2 className="help-section-title">Contacto</h2>
              <div className="contact-info">
                <div className="contact-grid">
                  {settings.contact.address && (
                    <div className="contact-card">
                      <div className="contact-card-icon">
                        <LocationIcon size={28} />
                      </div>
                      <div className="contact-card-content">
                        <h4>Dirección</h4>
                        <p>{settings.contact.address}</p>
                      </div>
                    </div>
                  )}
                  {settings.contact.phone && (
                    <div className="contact-card">
                      <div className="contact-card-icon">
                        <PhoneIcon size={28} />
                      </div>
                      <div className="contact-card-content">
                        <h4>Teléfono</h4>
                        <p>
                          <a href={`tel:${settings.contact.phone.replace(/\s/g, '')}`}>
                            {settings.contact.phone}
                          </a>
                        </p>
                      </div>
                    </div>
                  )}
                  {settings.contact.email && (
                    <div className="contact-card">
                      <div className="contact-card-icon">
                        <EmailIcon size={28} />
                      </div>
                      <div className="contact-card-content">
                        <h4>Email</h4>
                        <p>
                          <a href={`mailto:${settings.contact.email}`}>
                            {settings.contact.email}
                          </a>
                        </p>
                      </div>
                    </div>
                  )}
                  {settings.contact.schedule && (
                    <div className="contact-card">
                      <div className="contact-card-icon">
                        <ClockIcon size={28} />
                      </div>
                      <div className="contact-card-content">
                        <h4>Horario de Atención</h4>
                        <p>{settings.contact.schedule}</p>
                      </div>
                    </div>
                  )}
                </div>
                {/* Redes sociales */}
                {(settings.contact.socialMedia?.facebook || 
                  settings.contact.socialMedia?.instagram || 
                  settings.contact.socialMedia?.whatsapp) && (
                  <div className="contact-social-media">
                    <h3 className="social-media-title">Síguenos en nuestras redes</h3>
                    <div className="social-links">
                      {settings.contact.socialMedia.facebook && (
                        <a 
                          href={settings.contact.socialMedia.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-link social-facebook"
                          title="Facebook"
                          aria-label="Facebook"
                        >
                          <FacebookIcon className="social-icon-svg" size={28} />
                          <span>Facebook</span>
                        </a>
                      )}
                      {settings.contact.socialMedia.instagram && (
                        <a 
                          href={settings.contact.socialMedia.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-link social-instagram"
                          title="Instagram"
                          aria-label="Instagram"
                        >
                          <InstagramIcon className="social-icon-svg" size={28} />
                          <span>Instagram</span>
                        </a>
                      )}
                      {buildWhatsAppLinkAR(settings.contact.socialMedia.whatsapp) && (
                        <a 
                          href={buildWhatsAppLinkAR(settings.contact.socialMedia.whatsapp)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-link social-whatsapp"
                          title="WhatsApp"
                          aria-label="WhatsApp"
                        >
                          <WhatsAppIcon className="social-icon-svg" size={28} />
                          <span>WhatsApp</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )
        )}
      </div>
    </div>
  )
}

export default Help

